import Icon, { IconType } from 'components/Icon';
import { CommentFrameEntry, Frame } from 'dragonmouse-shared/interfaces';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Comment from './Comment';
import CommentInput from './CommentInput';
import * as S from './styles';

interface CommentsProps {
	frame: Frame;
	filterElementId?: string;
}

const Comments = ({ frame, filterElementId }: CommentsProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const theme = useTheme();
	const [replyingToId, setReplyingToId] = useState<string | null>(null);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	// All non-deleted comment entries, sorted chronologically
	const allComments = useMemo(
		() =>
			(frame.entries || [])
				.filter((entry): entry is CommentFrameEntry => entry.kind === 'comment' && !entry.deletedAt)
				.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
		[frame.entries]
	);

	// Top-level comments (no parentId), optionally filtered by elementId
	const topLevelComments = useMemo(
		() =>
			allComments
				.filter((c) => !c.parentId)
				.filter((c) => (filterElementId ? c.elementId === filterElementId : true)),
		[allComments, filterElementId]
	);

	// Replies grouped by parent id
	const repliesByParentId = useMemo(() => {
		const map = new Map<string, CommentFrameEntry[]>();
		for (const c of allComments) {
			if (c.parentId) {
				const list = map.get(c.parentId) ?? [];
				list.push(c);
				map.set(c.parentId, list);
			}
		}
		return map;
	}, [allComments]);

	// scroll to bottom when a new comment is added
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
		}
	}, [allComments.length]);

	// scroll to bottom when the panel is shown
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
		}
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onStartReply = (commentId: string) => setReplyingToId(commentId);
	const onCancelReply = () => setReplyingToId(null);
	const onSubmitReply = () => setReplyingToId(null);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderThread = (parent: CommentFrameEntry) => {
		const replies = repliesByParentId.get(parent.id) ?? [];
		return (
			<S.Thread key={parent.id}>
				<Comment comment={parent} frame={frame} onStartReply={() => onStartReply(parent.id)} />
				{(replies.length > 0 || replyingToId === parent.id) && (
					<S.ReplyList>
						{replies.map((reply) => (
							<Comment key={reply.id} comment={reply} frame={frame} isReply />
						))}
						{replyingToId === parent.id && (
							<CommentInput
								frame={frame}
								elementId={parent.elementId ?? undefined}
								parentId={parent.id}
								autoFocus
								onCancel={onCancelReply}
								onSubmit={onSubmitReply}
							/>
						)}
					</S.ReplyList>
				)}
			</S.Thread>
		);
	};

	const renderEmptyState = () => {
		return (
			<S.EmptyState>
				<S.EmptyStateIcon>
					<Icon type={IconType.CHAT} width={32} height={32} color={theme.gray[400]} />
				</S.EmptyStateIcon>
				<S.EmptyStateText>No comments yet</S.EmptyStateText>
				<S.EmptyStateSubtext>Enter a comment below to start the conversation.</S.EmptyStateSubtext>
			</S.EmptyState>
		);
	};

	return (
		<S.Base>
			<S.ScrollingArea ref={containerRef}>
				{topLevelComments.length > 0 ? topLevelComments.map(renderThread) : renderEmptyState()}
			</S.ScrollingArea>
			<CommentInput frame={frame} elementId={filterElementId} />
		</S.Base>
	);
};

export default Comments;
